import {PropsWithChildren} from 'react';
import {useTranslation} from 'react-i18next';
import {useTheme} from 'styled-components';
import ChekinProSettings from 'ChekinProSettings';
import {useGuestGroup, useReservationSettings} from 'hooks';
import Button from 'components/common/Button';
import {Crown} from 'lucide-react';
import {GuestItem} from 'components/GuestsPanel/components/GuestItem';
import {MainLoader} from 'components/common/loaders';
import {LoaderWrapper} from './styled';
import {
  Title,
  SubTitle,
  StyledIcon,
  RegisterButton,
  ButtonsWrapper,
  GuestPanelContainer,
  ShareRegistrationLinkButton,
} from '../styled';

const PanelGroupTitle = ({children}: PropsWithChildren) => {
  return (
    <div
      className={`text-sm font-semibold leading-6 mb-2.5 flex gap-2 items-center text-[var(--font-secondary)] uppercase`}
    >
      {children}
    </div>
  );
};

type Props = {
  handleRegisterNewGuest?: (isLeader?: boolean) => void;
  nextPage: string;
};
function GuestsList({handleRegisterNewGuest, nextPage}: Props) {
  const {guestGroup, isLoading, isFetching} = useGuestGroup();
  const hasNoGuests = guestGroup?.added_number_of_guests === 0;
  const theme = useTheme();
  const {t} = useTranslation();
  const {checkCanShowRegisterButton} = useReservationSettings();
  const isRegisterGuestButtonVisible = checkCanShowRegisterButton();
  const members = guestGroup?.members;
  const leaderGuest = members?.find(member => member.id === guestGroup?.leader_id);
  const accompanyingMembers = members?.filter(
    member => member.id !== guestGroup?.leader_id,
  );
  const knownNumberOfGuests = guestGroup?.known_number_of_guests ?? 0;
  const showAccompanyingGuests = knownNumberOfGuests > 1;

  if (isLoading) {
    return (
      <LoaderWrapper>
        <MainLoader visible width={80} />
      </LoaderWrapper>
    );
  }

  return (
    <>
      {hasNoGuests ? (
        <>
          <StyledIcon
            name={'iconRegister'}
            color={theme.colors.branding.primary}
            custom
            size={200}
            className="guest-register-view__icon-register"
          />
          <Title className="guest-register-view__no-guests-title">
            {t('no_registered_guest')}
          </Title>
          <SubTitle className="guest-register-view__no-guests-subtitle">
            {t('click_register_guest')}
          </SubTitle>
          <ButtonsWrapper>
            <Button loading={isFetching} label={t('register_guest')} to={nextPage} />
            {ChekinProSettings.canShareRegistrationLink && (
              <ShareRegistrationLinkButton />
            )}
          </ButtonsWrapper>
        </>
      ) : (
        <>
          <PanelGroupTitle>
            {t('guest_in_charge')}
            <Crown
              size="20"
              color="#FFD166"
              fill="#FFD166"
              className="top-[-1px] relative"
            />
          </PanelGroupTitle>
          <GuestPanelContainer className="guest-register-view__guest-panel">
            {leaderGuest && (
              <GuestItem
                isLoading={isFetching}
                guestMember={{...leaderGuest, isLeader: true}}
              />
            )}
          </GuestPanelContainer>
          {showAccompanyingGuests &&
            accompanyingMembers &&
            accompanyingMembers.length > 0 && (
              <>
                <PanelGroupTitle>{t('accompanying_guests')}</PanelGroupTitle>
                <GuestPanelContainer className="guest-register-view__guest-panel">
                  {accompanyingMembers.map((member, index) => (
                    <GuestItem isLoading={isFetching} guestMember={member} key={index} />
                  ))}
                </GuestPanelContainer>
              </>
            )}
          {isRegisterGuestButtonVisible && (
            <RegisterButton
              to={nextPage}
              loading={isFetching}
              onClick={() => handleRegisterNewGuest?.(members?.length === 0)}
              label={t('register_another_guest')}
              className="guest-register-view__register-btn"
            />
          )}
          {ChekinProSettings.canShareRegistrationLink && (
            <ShareRegistrationLinkButton className="guest-register-view__share-btn" />
          )}
        </>
      )}
    </>
  );
}

export {GuestsList};
